import { useState, useEffect } from "react";
import { Modal, Checkbox, Loading, Select } from "barte-design-system";
import { Input } from "@/components";
import { useUpdateUser } from "@/services/usersService";
import { showToast } from "@/utils";
import { useUserForm } from "../../hooks";
import { buildUpdatePayload } from "../../helpers";
import { STATUS_OPTIONS } from "../../constants";
import { UserPermissionsInfo } from "../user-permissions-info";
import type { ModalEditUserProps } from "./modal-edit-user.type";
import styles from "./modal-edit-user.module.css";

const PLACEHOLDER_OPTION = { label: "Selecione um perfil", value: "", disabled: true };

export const ModalEditUser = ({ isOpen, onClose, user }: ModalEditUserProps) => {
  const userInfo = JSON.parse(localStorage.getItem("barte-user-info") || "{}");
  const hasBankingAccess =
    userInfo?.bankingStatus === "ACTIVE" || userInfo?.roles?.includes("B_SELLER_ADMIN");

  const {
    formState,
    setField,
    hasBankingProfile,
    hasAcquiringProfile,
    acquiringProfileOptions,
    bankingProfileOptions,
    isLoading,
  } = useUserForm({ mode: "edit", user: user ?? undefined, isOpen });

  const [hasCheckedBanking, setHasCheckedBanking] = useState(false);
  const [hasCheckedAcquiring, setHasCheckedAcquiring] = useState(false);

  const { mutateAsync: updateUser, isPending: isUpdating } = useUpdateUser();

  useEffect(() => {
    if (!isLoading && isOpen) {
      setHasCheckedBanking(hasBankingProfile);
      setHasCheckedAcquiring(hasAcquiringProfile);
    }
  }, [isLoading, isOpen, hasBankingProfile, hasAcquiringProfile]);

  useEffect(() => {
    if (!isOpen) {
      setHasCheckedBanking(false);
      setHasCheckedAcquiring(false);
    }
  }, [isOpen]);

  const handleToggleBanking = () => {
    const next = !hasCheckedBanking;
    setHasCheckedBanking(next);
    if (!next) setField("bankingProfile", "");
  };

  const handleToggleAcquiring = () => {
    const next = !hasCheckedAcquiring;
    setHasCheckedAcquiring(next);
    if (!next) setField("acquiringProfile", "");
  };

  const handleSave = async () => {
    if (!user) return;

    const payload = buildUpdatePayload(
      hasCheckedAcquiring ? formState.acquiringProfile : "",
      hasCheckedBanking ? formState.bankingProfile : "",
      formState.status,
    );

    const response = await updateUser({ userId: user.id, payload });

    if (response.success) {
      showToast({ type: "success", message: "Usuário atualizado com sucesso." });
      onClose();
    } else {
      showToast({ type: "error", message: response.message });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title="Editar Usuário"
      onClose={onClose}
      footer={{
        primaryButton: {
          children: "Salvar",
          size: "sm",
          onClick: handleSave,
          loading: isUpdating,
        },
        secondaryButton: {
          children: "Cancelar",
          size: "sm",
          variant: "outline",
          onClick: onClose,
        },
      }}
    >
      {isLoading ? (
        <div className={styles.loading}>
          <Loading type="page" />
        </div>
      ) : (
        <>
          <div className={styles["email-field"]}>
            <Input
              label="Email"
              name="email"
              isDisabled
              placeholder="Email"
              value={formState.email}
            />
          </div>

          <div className={styles["profiles-section"]}>
            {hasBankingAccess && (
              <>
                <Checkbox
                  checked={hasCheckedBanking}
                  onChange={handleToggleBanking}
                  label="Banking"
                />
                {hasCheckedBanking && (
                  <div>
                    <Select
                      label="Perfis"
                      options={[PLACEHOLDER_OPTION, ...bankingProfileOptions]}
                      value={formState.bankingProfile}
                      onChange={(val: string) => setField("bankingProfile", val)}
                      placeholder="Selecione um perfil"
                    />
                  </div>
                )}
              </>
            )}

            <Checkbox
              checked={hasCheckedAcquiring}
              onChange={handleToggleAcquiring}
              label="Adquirência"
            />
            {hasCheckedAcquiring && (
              <div>
                <Select
                  label="Perfis"
                  options={[PLACEHOLDER_OPTION, ...acquiringProfileOptions]}
                  value={formState.acquiringProfile}
                  onChange={(val: string) => setField("acquiringProfile", val)}
                  placeholder="Selecione um perfil"
                />
              </div>
            )}
          </div>

          <div className={styles["status-field"]}>
            <Select
              label="Status"
              options={STATUS_OPTIONS}
              value={formState.status}
              onChange={(val: string) => setField("status", val)}
              placeholder="Selecione o status"
            />
          </div>
        </>
      )}
      <UserPermissionsInfo
        acquiringSelectedProfile={formState.acquiringProfile}
        bankingSelectedProfile={formState.bankingProfile}
      />
    </Modal>
  );
};
